import { sumCents } from '../../shared/money/cents.js'
import type { Cents } from '../../shared/money/cents.js'
import type { CashImpact } from './cash-impact.js'
import { grupoDeParcelas } from './parcelamento.js'
import type { Transaction } from './transaction.js'

/**
 * O abatimento de um gasto por entradas — `Docs/05-Plano/Abatimento de gasto.md`.
 *
 * O churrasco de R$ 200 e os três Pix de R$ 50 que os amigos mandaram: sem
 * isto, a casa aparece tendo gastado 200 e recebido 150, quando gastou 50 e não
 * recebeu nada. Com isto, as entradas ligadas saem de Entradas e o gasto vale o
 * líquido — **na competência**. O caixa não muda: o dinheiro se moveu.
 *
 * ## Quem escreve o quê
 *
 * - A ENTRADA guarda `enrichment.offsetOf`, escrito pelo app. É a única fonte
 *   do vínculo: as duas direções da tela gravam só ali.
 * - O GASTO guarda `enrichment.offset`, derivado, escrito só pelo gatilho
 *   `onTransactionWrite` — pela função `planoDoAbatimento` daqui, a mesma que a
 *   dublê de teste usa.
 *
 * ## A compra, e não a linha
 *
 * As parcelas de uma compra dividem categoria, observação e competência
 * (decisão 277), e o abatimento segue: as entradas abatem a COMPRA, e o
 * gatilho reparte o total entre as parcelas.
 */

export type OffsetLink = NonNullable<Transaction['enrichment']['offsetOf']>
export type Offset = NonNullable<Transaction['enrichment']['offset']>

/**
 * O mínimo que a conta precisa de uma linha — e o que o gatilho consegue
 * montar de um documento cru do Admin SDK, sem o adapter do cliente.
 */
export type LinhaDoAbatimento = {
  id: string
  amountCents: Cents
  status: Transaction['status']
  cashImpact: CashImpact
}

/**
 * A identidade da compra: o grupo de parcelas, ou a própria linha à vista. A
 * mesma pergunta de `chaveDeDiscussao`, e uma função irmã — e não a mesma —
 * porque aquela é documentada como opaca e de uso único.
 */
export function chaveDaCompra(t: Transaction): string {
  return grupoDeParcelas(t) ?? t.id
}

/**
 * Como a COMPRA se chama no vínculo: a parcelada perde o "n/m" que cada
 * parcela traz na descrição (`YOUCOM 3/4` → `YOUCOM`). O vínculo é da compra,
 * e guardar o texto de uma parcela fazia a lista dizer "abate YOUCOM 3/4" para
 * um Pix que pagou as quatro. À vista fica como veio — um "10/10" ali não é
 * parcela de nada.
 */
export function descricaoDaCompra(t: Transaction): string {
  const texto = t.source.description
  if (t.source.installment === null) return texto
  return texto.replace(/\s*\d{1,2}\s*\/\s*\d{1,2}\s*$/, '').trim() || texto
}

export function linhaDe(t: Transaction): LinhaDoAbatimento {
  return { id: t.id, amountCents: t.source.amountCents, status: t.status, cashImpact: t.cashImpact }
}

/**
 * As duas que são consumo. Pagamento de fatura (`settlement`) e espelho de
 * movimento (`internal`) não são — abatê-los tiraria das somas um dinheiro que
 * nunca esteve nelas.
 */
function ehConsumo(cashImpact: CashImpact): boolean {
  return cashImpact === 'direct' || cashImpact === 'deferred'
}

/** Uma entrada que pode abater: positiva, viva, de consumo. */
export function podeAbater(l: LinhaDoAbatimento): boolean {
  return l.amountCents > 0 && l.status !== 'canceled' && ehConsumo(l.cashImpact)
}

/** Um gasto que pode ser abatido: negativo, vivo, de consumo. */
export function podeSerAbatido(l: LinhaDoAbatimento): boolean {
  return l.amountCents < 0 && l.status !== 'canceled' && ehConsumo(l.cashImpact)
}

/**
 * `totalCents` repartido na proporção de `pesos`, pelo **maior resto**: cada
 * parte é o piso da conta exata, e os centavos que faltam vão, um a um, para os
 * maiores restos — empate desempata pela ordem, para o resultado ser estável.
 *
 * A soma das partes é **sempre** `totalCents`, e com `totalCents <= soma dos
 * pesos` nenhuma parte passa do seu peso. É o que deixa a soma das parcelas, e
 * a das fatias da rosca, fechar no centavo.
 */
export function repartirNaProporcao(totalCents: Cents, pesos: readonly Cents[]): Cents[] {
  const soma = sumCents([...pesos])
  if (soma <= 0 || totalCents <= 0) return pesos.map(() => 0)

  const exatas = pesos.map((p) => (totalCents * p) / soma)
  const partes = exatas.map((e) => Math.floor(e))
  let falta = totalCents - sumCents(partes)
  const ordem = exatas
    .map((e, i) => ({ i, resto: e - Math.floor(e) }))
    .sort((a, b) => b.resto - a.resto || a.i - b.i)
  for (const { i } of ordem) {
    if (falta <= 0) break
    partes[i] = (partes[i] ?? 0) + 1
    falta -= 1
  }
  return partes
}

export type PlanoDoAbatimento = {
  /** O `offset` que cada linha recebida deve ter — inclusive as que ficam `null`. */
  offsets: Map<string, Offset | null>
  /** Entradas cujo `offsetOf` deve ser limpo. */
  desligar: string[]
  abatidoCents: Cents
  /** Quanto as entradas passam da compra. Zero no caso normal. */
  excessoCents: Cents
}

/**
 * O que o gatilho grava numa compra, dadas as linhas dela e as entradas que
 * apontam para ela.
 *
 * - Entrada que deixou de ser elegível (cancelada, virou saída) é desligada.
 * - Compra sem linha viva (cancelada inteira, ou sumiu): **todas** as entradas
 *   são desligadas e voltam a ser entradas comuns — sem isso o dinheiro sumiria
 *   de todo relatório.
 * - Entradas que passam da compra: o abatimento é limitado ao valor dela, e o
 *   excesso volta dito. Nenhuma entrada é desligada por isso — quem decide é a
 *   pessoa, que vê o excesso na tela.
 */
export function planoDoAbatimento(
  linhas: readonly LinhaDoAbatimento[],
  entradas: readonly LinhaDoAbatimento[],
): PlanoDoAbatimento {
  const vivas = linhas.filter(podeSerAbatido)
  const validas = entradas.filter(podeAbater)
  const offsets = new Map<string, Offset | null>(linhas.map((l) => [l.id, null]))

  if (vivas.length === 0) {
    return { offsets, desligar: entradas.map((e) => e.id), abatidoCents: 0, excessoCents: 0 }
  }

  const recebido = sumCents(validas.map((e) => e.amountCents))
  const valorDaCompra = sumCents(vivas.map((l) => -l.amountCents))
  const abatido = Math.min(recebido, valorDaCompra)
  const partes = repartirNaProporcao(
    abatido,
    vivas.map((l) => -l.amountCents),
  )
  vivas.forEach((l, i) => {
    const parte = partes[i] ?? 0
    if (parte > 0) offsets.set(l.id, { offsetCents: parte, entryCount: validas.length })
  })

  return {
    offsets,
    desligar: entradas.filter((e) => !podeAbater(e)).map((e) => e.id),
    abatidoCents: abatido,
    excessoCents: recebido - abatido,
  }
}

/**
 * O valor que conta na **competência**: o gasto menos o que abateram; a
 * entrada ligada, zero. O caixa não passa por aqui.
 */
export function liquidoDe(t: Transaction): Cents {
  if (t.enrichment.offsetOf !== null) return 0
  return t.source.amountCents + (t.enrichment.offset?.offsetCents ?? 0)
}

export type Contribuicao = { categoryId: string | null; cents: Cents }

/**
 * As contribuições de um gasto por categoria — itens do detalhamento mais o
 * resto —, reduzidas para somar `liquidoCents` (em módulo). Uma compra de
 * R$ 300 em mercado e limpeza, abatida em R$ 300, zera as duas.
 */
export function reduzirNaProporcao(
  contribuicoes: readonly Contribuicao[],
  liquidoCents: Cents,
): Contribuicao[] {
  const partes = repartirNaProporcao(
    liquidoCents,
    contribuicoes.map((c) => c.cents),
  )
  return contribuicoes
    .map((c, i) => ({ categoryId: c.categoryId, cents: partes[i] ?? 0 }))
    .filter((c) => c.cents > 0)
}

/**
 * Falta alguém dar categoria a esta transação?
 *
 * A entrada ligada **segue o gasto** — a categoria dela não conta em soma
 * nenhuma. E o gasto que as entradas zeraram não é consumo de ninguém da casa:
 * pedir categoria para ele é o trabalho que o abatimento existe para poupar.
 */
export function precisaDeCategoria(t: Transaction): boolean {
  if (t.enrichment.categoryId !== null) return false
  if (t.enrichment.offsetOf !== null) return false
  if (t.enrichment.offset !== null && liquidoDe(t) === 0) return false
  return true
}
